import {
  Button,
  InputAdornment,
  InputBase,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { AiPromptTemplate } from 'src/@types/ai';
import { AiBackground } from 'src/components/ai/AiBackground';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { useAiTranscriptionMutation } from './useAiTranscriptionMutation';

type Props = {
  transcriptionId: string;
  onAddToEditor: (text: string) => void;
};

export const CoperaAiTranscriptionActions = ({
  transcriptionId,
  onAddToEditor,
}: Props) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [input, setInput] = useState<string>('');

  const open = Boolean(anchorEl);

  const { sendToAi, isSendingToAi } = useAiTranscriptionMutation();
  const { translate } = useLocales();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setInput(event.target.value);
  };

  const sendToAI = async () => {
    await handleAiAction(
      AiPromptTemplate.TRANSCRIPTION_AI_ACTION_OWN_PROMPT,
      input,
    );
  };

  const handleAiAction = async (template: AiPromptTemplate, input?: string) => {
    handleClose();
    const { text } = await sendToAi({ transcriptionId, template, input });
    onAddToEditor(text);
  };

  const OPTIONS: {
    label: string;
    icon: string;
    action: AiPromptTemplate;
    loading?: boolean;
  }[] = [
    {
      label: translate('actions_bar.copera_ai_notes_actions.summarize'),
      icon: 'tabler:blockquote',
      action: AiPromptTemplate.SUMMARIZE_TRANSCRIPTION,
    },
    {
      label: translate(
        'actions_bar.copera_ai_notes_actions.find_actions_items',
      ),
      icon: 'tabler:list-check',
      action: AiPromptTemplate.FIND_ACTIONS_ITEMS,
    },
    {
      label: translate('actions_bar.copera_ai_notes_actions.brainstorm_ideas'),
      icon: 'tabler:bulb',
      action: AiPromptTemplate.BRAINSTORM_IDEAS,
    },
    {
      label: translate('actions_bar.copera_ai_notes_actions.to_do_list'),
      icon: 'tabler:circle-check',
      action: AiPromptTemplate.TO_DO_LIST,
    },
    {
      label: translate('actions_bar.copera_ai_notes_actions.meeting_agenda'),
      icon: 'tabler:pencil-minus',
      action: AiPromptTemplate.MEETING_AGENDA,
    },
    {
      label: translate(
        'actions_bar.copera_ai_notes_actions.pros_and_cons_list',
      ),
      icon: 'tabler:pencil-minus',
      action: AiPromptTemplate.PROS_AND_CONS_LIST,
    },
  ];

  return (
    <>
      <StyledButton
        size="small"
        variant="contained"
        color="primary"
        onClick={handleClick}
        disabled={isSendingToAi}
        startIcon={
          <Iconify
            icon={isSendingToAi ? 'eos-icons:loading' : 'tabler:sparkles'}
          />
        }
        endIcon={
          <Iconify
            icon="tabler:chevron-down"
            width={18}
            height={18}
            zIndex={1}
          />
        }
      >
        <Typography
          variant="caption"
          fontWeight={600}
          minWidth={105}
          zIndex={1}
        >
          {isSendingToAi
            ? translate('shared.loading.loading_action')
            : translate('shared.ai_copera.copera_ai_actions')}
        </Typography>
        {isSendingToAi && <StyledAiBackground />}
      </StyledButton>

      <Menu
        id="header-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Stack sx={{ padding: theme.spacing(1) }}>
          <InputBase
            value={input}
            placeholder={translate(
              'shared.input.placeholder.write_your_prompt',
            )}
            onChange={handleInputChange}
            onKeyDown={async (e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                await sendToAI();
              }
            }}
            startAdornment={
              <InputAdornment position="start">
                <Iconify
                  icon="tabler:sparkles"
                  color={theme.palette.primary.main}
                />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <Button
                  variant="contained"
                  size="small"
                  onClick={sendToAI}
                  disabled={!input}
                  sx={{
                    minWidth: 'unset',
                    height: 'unset',
                    padding: theme.spacing(0.25),
                  }}
                >
                  <Iconify icon="tabler:arrow-up" color="white" />
                </Button>
              </InputAdornment>
            }
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: theme.spacing(1),
              width: '100%',
              flexShrink: 0,
              fontSize: theme.typography.body2.fontSize,
              padding: theme.spacing(0.5, 1),
            }}
          />
        </Stack>

        <Stack sx={{ padding: theme.spacing(0.75, 2) }}>
          <Typography
            variant="caption"
            color={theme.palette.action.disabled}
            fontWeight={600}
          >
            {translate('shared.ai_copera.draft_with_ai')}
          </Typography>
        </Stack>

        {OPTIONS.map((option, index) => (
          <MenuItem
            onClick={async () => {
              await handleAiAction(option.action);
            }}
            key={`folder-dropdown-${index}`}
            color="text.primary"
            sx={{ gap: 1 }}
          >
            <Iconify icon={option.icon} color={theme.palette.primary.main} />
            <Typography variant="body2" color="text.secondary">
              {option.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const StyledAiBackground = styled(AiBackground)(() => ({
  position: 'absolute',
  inset: 0,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  paddingBlock: theme.spacing(3),
  [theme.breakpoints.up('sm')]: {
    paddingBlock: 0,
  },
}));
